import { Component, HostBinding, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA, MatPaginator, MatSort } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { DataTableSource } from '../../../classes/DataTableSource/datatablesource';

import { NotificationItem, NotificationTermInfo } from '../../notification/notification.service';
import { FormStatusPipe } from '../../../pipes/formstatus.pipe';

@Component({
    selector: 'dialog-notification-component',
    styleUrls: ['notification.dialogcomponent.css'],
    templateUrl: 'notification.dialogcomponent.html',
})
export class NotificationDialogComponent implements OnDestroy, OnInit {
    @HostBinding('class.smalldisplay') SmallDisplay: boolean;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    CurrentDetails: NotificationTermInfo[];
    IsAdoptions: boolean;
    LastSizeCode: string;
    TitleText: string;
    MessageText: string;
    Notifications: NotificationItem[];
    DetailsData: DataTableSource<NotificationTermInfo>;
    IsAdvisor: boolean;
    ShowingFullDetail: boolean;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        public dialogRef: MatDialogRef<NotificationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: BaseNotificationDialogOptions,
        private mediaChange: ObservableMedia,
        private formStatusPipe: FormStatusPipe,
        private router: Router
    ) {
        this.TitleText = data.TitleText || 'Notification Details...';
        this.MessageText = data.MessageText || '';
        this.Notifications = data.Notifications || [];
        this.IsAdoptions = data.IsAdoptions != null ? data.IsAdoptions : false;
        this.IsAdvisor = data.IsAdvisor || false;
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.CurrentDetails = null;

        self.LastSizeCode = self.mediaChange.isActive('xs')
            ? 'xs'
            : self.mediaChange.isActive('sm')
            ? 'sm'
            : self.mediaChange.isActive('md')
            ? 'md'
            : self.mediaChange.isActive('lg')
            ? 'lg'
            : 'xl';
        self.SmallDisplay = self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm';
        self.VisibleHeaders = self.SmallDisplay
            ? ['SmallDisplay']
            : self.IsAdoptions
            ? ['RequestNumber', 'RequestorName', 'Code', 'Name', 'DateRequested', 'Actions']
            : self.IsAdvisor
                ? ['StudentName', 'StudentID', 'College', 'CollegeTerm', 'SchoolName', 'FormStatus', 'DateUpdated', 'Actions']
                : ['StudentName', 'StudentID', 'College', 'CollegeTerm', 'DateUpdated', 'Actions']
        ;
        self.VisibleColumns = self.VisibleHeaders.slice();

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.LastSizeCode = media.mqAlias;
                self.VisibleHeaders = self.SmallDisplay
                    ? ['SmallDisplay']
                    : self.IsAdoptions
                    ? ['RequestNumber', 'RequestorName', 'Code', 'Name', 'DateRequested', 'Actions']
                    : self.IsAdvisor
                        ? ['StudentName', 'StudentID', 'College', 'CollegeTerm', 'SchoolName', 'FormStatus', 'DateUpdated', 'Actions']
                        : ['StudentName', 'StudentID', 'College', 'CollegeTerm', 'DateUpdated', 'Actions']
                ;
                self.VisibleColumns = self.VisibleHeaders.slice();
            });
            
        self.DetailsData = new DataTableSource<NotificationTermInfo>([], self.paginator, self.sorter, self.SortNotifications.bind(this));
        this.ShowingFullDetail = false;
    }

    // #region OK Handler

    OKHandler() {
        this.dialogRef.close(<NotificationTermInfo>null);
    }

    // #endregion

    // #region Go Back Method

    GoBack() {
        this.ShowingFullDetail = false;
    }

    // #endregion

    // #region Show Notification Details Method

    ShowNotificationDetails(detail: NotificationItem) {
        // Load the current details into the details database
        this.DetailsData.Update(detail.Details);
        this.ShowingFullDetail = true;
    }

    // #endregion

    // #region Sort Notifications Method

    SortNotifications(First: NotificationTermInfo, Second: NotificationTermInfo) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'StudentName':
                [PropA, PropB] = [First.StudentName, Second.StudentName];
                break;
            case 'StudentID':
                [PropA, PropB] = [First.StudentID, Second.StudentID];
                break;
            case 'SchoolName':
                [PropA, PropB] = [First.SchoolName || "", Second.SchoolName || ""];
                break;
            case 'FormStatus':
                [PropA, PropB] = [First.FormStatus, Second.FormStatus];
                break;
            // case 'FormStatus':
            //     [PropA, PropB] = [
            //         self.formStatusPipe.transform(First, self.FormStatus),
            //         self.formStatusPipe.transform(Second, self.FormStatus),
            //     ];
            //     break;
        }

        const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
        const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

        return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
    }

    // #endregion

    // #region View Item Method

    ViewItem(item: NotificationTermInfo) {
        const result = <NotificationTermInfo>{
            DualEnrollStudentID: item.DualEnrollStudentID,
            StudentName: item.StudentName,
            StudentID: item.StudentID,
            CollegeID: item.CollegeID,
            Term: item.Term,
            ActionItemID: item.ActionItemID,
            ActionItemName: item.ActionItemName,
        };

        this.dialogRef.close(result);
    }

    // #endregion
}

// #region Custom Classes

// #region Notification Dialog Options Class

export class BaseNotificationDialogOptions {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    IconColor?: string;
    IconName?: string;
    ShowIcon?: boolean;
    height?: string;
    width?: string;
    Notifications?: NotificationItem[];
    IsAdoptions?: boolean;
    IsAdvisor?: boolean;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/services/dialogs/notification/notification.dialogcomponent.ts